import type { HeadersFunction, LoaderArgs, MetaFunction } from "@remix-run/node";
import { json } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import clsx from "clsx";
import { fetchTopicsList } from "@/lib/microcms.server";
import { createOGP } from "@/utilities/ogp";
import { PageContainer } from "@/components/PageContainer";
import { ArticleLayout } from "@/components/ArticleLayout";
import { DisplayText } from "@/components/DisplayText";
import { TopicsList } from "@/components/Topics/List";
import { TopicsPagination } from "@/components/Topics/Pagination";
import { CacheRevalidateMinutes, notFound } from "@/lib/responses.server";
import common from "@/data/common.json";

export const meta: MetaFunction = () => {
  return createOGP({ path: "/topics", pageName: "トピックス" });
};

export const headers: HeadersFunction = () => {
  return {
    "Cache-Control": CacheRevalidateMinutes(),
  };
};

const PER_PAGE = 10;

export async function loader({ request }: LoaderArgs) {
  const url = new URL(request.url);
  const page = url.searchParams.get("page");
  const pageNum = page ? Number(page) : 1;
  if (isNaN(pageNum)) {
    throw notFound();
  }

  const topics = await fetchTopicsList((pageNum - 1) * PER_PAGE, PER_PAGE).catch(() => null);
  return json({
    topics: topics?.contents || [],
    totalCount: topics?.totalCount || 0,
    page: pageNum,
    canonical: `${common.meta.url}/topics`,
  });
}

export default function Topics() {
  const { topics, totalCount, page } = useLoaderData<typeof loader>();

  return (
    <PageContainer>
      <ArticleLayout
        paths={[
          { name: "ホーム", url: "/" },
          { name: "トピックス", url: "/topics" },
        ]}
      >
        <div className={clsx("flex", "flex-col", "space-y-8")}>
          <DisplayText element="h1" size="extraLarge">
            トピックス
          </DisplayText>
          <TopicsList topics={topics} />
          {totalCount > PER_PAGE && (
            <TopicsPagination baseUrl="/topics" per={PER_PAGE} totalCount={totalCount} page={page} />
          )}
        </div>
      </ArticleLayout>
    </PageContainer>
  );
}
